import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, } from "react";
import { loadTurnstile } from "../lib/turnstile-loader";
/**
 * Cloudflare Turnstile, rendered explicitly. The script is loaded once for the page by
 * `loadTurnstile`; the widget is removed on unmount. Browser-safe; strings arrive as props.
 */
export const TurnstileWidget = forwardRef(function TurnstileWidget({ siteKey, onToken, onError, onExpire, label, loadErrorText, theme = "auto" }, ref) {
    const containerRef = useRef(null);
    const apiRef = useRef(null);
    const widgetIdRef = useRef(null);
    const [loadFailed, setLoadFailed] = useState(false);
    // Latest callbacks without re-rendering the widget when a parent passes new closures.
    const handlers = useRef({ onToken, onError, onExpire });
    handlers.current = { onToken, onError, onExpire };
    useImperativeHandle(ref, () => ({
        reset() {
            handlers.current.onToken(null);
            if (apiRef.current !== null && widgetIdRef.current !== null) {
                apiRef.current.reset(widgetIdRef.current);
            }
        },
    }));
    useEffect(() => {
        let cancelled = false;
        const container = containerRef.current;
        if (container === null)
            return undefined;
        loadTurnstile().then((api) => {
            if (cancelled)
                return;
            apiRef.current = api;
            widgetIdRef.current = api.render(container, {
                sitekey: siteKey,
                theme,
                callback: (token) => {
                    handlers.current.onToken(token);
                },
                "expired-callback": () => {
                    handlers.current.onToken(null);
                    handlers.current.onExpire?.();
                },
                "error-callback": () => {
                    handlers.current.onToken(null);
                    handlers.current.onError?.();
                },
                "timeout-callback": () => {
                    handlers.current.onToken(null);
                },
            });
        }, () => {
            if (cancelled)
                return;
            setLoadFailed(true);
            handlers.current.onError?.();
        });
        return () => {
            cancelled = true;
            if (apiRef.current !== null && widgetIdRef.current !== null) {
                apiRef.current.remove(widgetIdRef.current);
            }
            widgetIdRef.current = null;
        };
    }, [siteKey, theme]);
    return (_jsxs("div", { role: "group", "aria-label": label, className: "flex flex-col gap-2", children: [_jsx("div", { ref: containerRef, "data-testid": "turnstile-container" }), loadFailed ? (_jsx("p", { role: "alert", className: "text-danger text-sm text-pretty", children: loadErrorText })) : null] }));
});
//# sourceMappingURL=turnstile-widget.js.map